/**
 * Composed-voxel mech (P3 — replaces `PlaceholderMech` in canonical use).
 *
 * Takes a `MechComposition` + the current `MechState` and renders a
 * single instanced-mesh group of unit cubes. We use `InstancedMesh`
 * per material slot so 4 draw calls handle a mech of any voxel count
 * — important for the perf target (PRD §6.2: 60 fps with several
 * hundred dynamic bodies later).
 *
 * Movement tweening matches `PlaceholderMech`. Per-part destruction
 * (P4) hides the corresponding voxels and spawns a chunk; that swap
 * happens in `BreakablePart` (see `physics/Debris.tsx`).
 *
 * Live material control (replay viz pass 2):
 *   - `hitPulse` triggers a ~250ms emissive flash on rising edge.
 *   - `mech.shutdown=true` dims the emissive slot to near-zero so a
 *     shutdown mech reads visually inert (the engine still resolves
 *     it; the viewer just stops it glowing).
 *
 * The flash + dim live in a single parent useFrame so the 4 child
 * InstancedMeshes don't each pay their own frame callback.
 */

import { useEffect, useMemo, useRef } from "react";
import {
  Color,
  InstancedMesh,
  Matrix4,
  type Group,
  type Material,
  type Mesh,
  type MeshBasicMaterial,
} from "three";
import { useFrame } from "@react-three/fiber";
import type { MechState } from "@pilot-protocol/replay-parser";
import { facingToYaw, hexToWorld } from "../scene/hexMath";
import type { MaterialSlot, MechComposition, PartSlot, Voxel } from "./types";
import { ALL_PARTS, exposedPartsOf, zoneToParts } from "./zoneMapping";
import { walkOffset } from "./walkCycle";
import { PIP_FIRE_FLASH_MS, PIP_LOCAL_Y, PIP_RADIUS, resolvePipState } from "./statusPip";
import { RECOIL_DURATION_MS, recoilOffset, recoilPitch } from "./recoil";
import { hitImpulse } from "./hitImpulse";
import { killChoreography } from "./killChoreography";
import { applyDissolvePatch, getDissolveUniforms } from "./dissolveMaterial";
import { assemblyState } from "./assemblyChoreography";
import { knockdownState } from "./knockdownProne";
import { restartArc, shutdownArc } from "./shutdownPunch";
import { staggerOffset } from "./staggerWobble";
import {
  EvasionMotes,
  PartSmoke,
  ScanLine,
  SmokeWisps,
  TaggedMarker,
  TargetLockReticle,
} from "./StateIndicators";
import { BurningAura, EmpAura, FrozenAura } from "./statusAuras";
import { HeatShimmer } from "./HeatShimmer";

interface ArchetypeMechProps {
  mech: MechState;
  composition: MechComposition;
  /**
   * Equipped cosmetic palette override. When a chassis_skin cosmetic
   * is equipped, the consumer resolves the SKU's `slot_palette` via
   * `@pilot-protocol/cosmetic-assets` and passes the resulting 4-color
   * map here. Any provided slot overrides the archetype default;
   * omitted slots fall through. Pass `undefined` (default) to keep
   * the archetype's stock palette — backwards-compatible.
   *
   * Decoupling: typed as `Partial<Record<MaterialSlot, string>>` so
   * the visual-engine package stays free of a `cosmetic-assets`
   * import. Both packages just agree on the 4-slot vocabulary.
   */
  paletteOverride?: Partial<Record<MaterialSlot, string>>;
  /** Tween time in ms between MechState updates. */
  tweenMs?: number;
  /** When true, fire a one-shot emissive flash. Rising-edge triggered. */
  hitPulse?: boolean;
  /**
   * Unit-vector direction the shot came from (shooter → victim in XZ
   * world space). Sampled on the rising edge of `hitPulse`; the
   * chassis is pushed along this vector by `hitImpulse`. Passing
   * `(0, 0)` (or omitting) still fires the scale-pulse + emissive
   * flash but skips the push.
   */
  hitNormal?: { dx: number; dz: number };
  /**
   * B6 — engine zone string (e.g. "head", "front_ct", "l_leg") for
   * the hit event that triggered this rising edge. When set, the
   * emissive flash localizes to the visual part(s) that map to that
   * zone (head → head part, *_leg → legs part, etc.). When omitted
   * or unrecognized, the whole chassis flashes as before.
   */
  hitZone?: string | null;
  /** When true, the mech has been destroyed at or before the current
   *  tick. Triggers a one-time fade-out + final hide on rising edge. */
  killed?: boolean;
  /** When true at the current tick, this side fired a weapon. Drives
   *  the white-flash on the status pip (FE-30 P4). Rising-edge. */
  justFired?: boolean;
  /** When true (a11y), skip motion-bearing effects (walk bob/tilt,
   *  status-pip pulse animation). Information-bearing effects (flash,
   *  kill-fade, heat glow, pip color signal) still fire. */
  reducedMotion?: boolean;
  /**
   * B4 status auras — the engine doesn't yet emit "frozen" so we
   * accept an opt-in prop. Burning + EMP read from MechState
   * directly (smoke_active, ecm_bubble_active).
   */
  frozen?: boolean;
  /**
   * B8 — when true, drive the assembly choreography (reverse dissolve
   * from scattered → intact) over ASSEMBLY_DURATION_MS. Rising edge
   * starts the animation; falling edge has no effect (the chassis
   * stays assembled).
   */
  assembling?: boolean;
}

const SLOT_ORDER: MaterialSlot[] = ["primary", "secondary", "accent", "emissive"];
// One voxel = 0.025 world units. Mech parts author at this 4×-resolution
// grid (vs the original 0.1 grid) so the chassis can carry seams,
// grilles, and chamfer detail without exploding the part count.
const VOXEL_SCALE = 0.025;

const FLASH_DURATION_MS = 220;
const FLASH_PEAK_INTENSITY_BONUS = 3.2;
const EMISSIVE_BASE_INTENSITY = 1.4;
const EMISSIVE_SHUTDOWN_INTENSITY = 0.12;

// Heat thresholds mirror the deterministic engine's resolver constants
// (HEAT_WARN_THRESHOLD / HEAT_DANGER_THRESHOLD / HEAT_SHUTDOWN_THRESHOLD).
// Kept inline because the visual engine doesn't import the engine module.
const HEAT_WARN = 20;
const HEAT_PEAK_FOR_GLOW = 30;

// Kill choreography — three-phase hold/flash/collapse arc lives in
// `./killChoreography` so we can unit-test the curve in pure Node.
// Walk-cycle bob (P1) lives in `./walkCycle` for the same reason.

// Warm-red tint blended into the emissive color as heat rises.
const HEAT_TINT_COLOR = new Color("#ff3322");
// B14 — deep-red breach glow for an exposed (armor=0) zone. Drawn as a
// low-intensity persistent emissive bonus on the affected parts so the
// breach reads even at idle. Pulses at ~1Hz so it doesn't blend into
// the static heat glow.
const BREACH_TINT_COLOR = new Color("#ff2a14");
const BREACH_BASE_BONUS = 0.55;
const BREACH_PULSE_AMP = 0.25;
const BREACH_PULSE_HZ = 1.0;
// White (1,1,1) — kill-flash blend target on the emissive slot.
const WHITE = new Color(1, 1, 1);
// Scratch color reused per frame to avoid GC churn.
const _scratchColor = new Color();

type MaterialHandle = Material & {
  color?: Color;
  emissive?: Color;
  emissiveIntensity?: number;
  metalness?: number;
  roughness?: number;
};

export function ArchetypeMech({
  mech,
  composition: compositionProp,
  paletteOverride,
  tweenMs = 400,
  hitPulse = false,
  hitNormal,
  hitZone = null,
  killed = false,
  justFired = false,
  reducedMotion = false,
  frozen = false,
  assembling = false,
}: ArchetypeMechProps) {
  // Fold the equipped-cosmetic palette override (if any) into the
  // composition once at the top so every downstream `composition
  // .palette[slot]` access naturally picks up the cosmetic tint. The
  // override is partial — omitted slots fall through to the archetype
  // default. Identity-stable when nothing's equipped so memoized
  // children below don't re-render on every frame.
  const composition = useMemo<MechComposition>(() => {
    if (!paletteOverride) return compositionProp;
    return {
      ...compositionProp,
      palette: { ...compositionProp.palette, ...paletteOverride },
    };
  }, [compositionProp, paletteOverride]);
  const groupRef = useRef<Group>(null);
  const hitNormalRef = useRef<{ dx: number; dz: number }>({ dx: 0, dz: 0 });
  // B6 — per-part × per-slot meshes. Hit/destruction events can target
  // a specific part's emissive without lighting the whole chassis.
  // We pre-seed every (part × slot) cell with `null` so the inner
  // useFrame loop can index without conditional fallback.
  const meshRefs = useRef<Record<PartSlot, Record<MaterialSlot, InstancedMesh | null>>>({
    chassis: { primary: null, secondary: null, accent: null, emissive: null },
    legs: { primary: null, secondary: null, accent: null, emissive: null },
    left_arm: { primary: null, secondary: null, accent: null, emissive: null },
    right_arm: { primary: null, secondary: null, accent: null, emissive: null },
    head: { primary: null, secondary: null, accent: null, emissive: null },
    mount: { primary: null, secondary: null, accent: null, emissive: null },
  });
  // Parts to localize the flash on the latest hit. Sampled on rising
  // edge of hitPulse alongside hitNormalRef.
  const flashPartsRef = useRef<ReadonlyArray<PartSlot> | null>(null);
  const baseEmissiveColorRef = useRef<Color>(new Color(composition.palette.emissive));
  const flashStartRef = useRef<number | null>(null);
  const prevHitPulseRef = useRef<boolean>(false);
  const killStartRef = useRef<number | null>(null);
  const prevKilledRef = useRef<boolean>(false);
  const fireFlashStartRef = useRef<number | null>(null);
  // P2 knockdown — fall timer rises with prone=true; recover timer
  // rises with prone=false (and only after a fall). Both `null` when
  // the chassis is upright and has never been knocked down.
  const knockdownFallStartRef = useRef<number | null>(null);
  const knockdownRecoverStartRef = useRef<number | null>(null);
  const prevKnockedDownRef = useRef<boolean>(false);
  // P2 shutdown / restart — start timer on the matching rising edge
  // so the emissive arcs run as one-shot events on top of the
  // persistent `mech.shutdown` state.
  const shutdownStartRef = useRef<number | null>(null);
  const restartStartRef = useRef<number | null>(null);
  const prevShutdownRef = useRef<boolean>(false);
  // B8 assembly — start timer on rising edge of `assembling`. The
  // dissolve uProgress runs 1 → 0 over the assembly window.
  const assemblyStartRef = useRef<number | null>(null);
  const prevAssemblingRef = useRef<boolean>(false);
  const prevJustFiredRef = useRef<boolean>(false);
  const pipMeshRef = useRef<Mesh>(null);
  // Walk-cycle bob (P1) — the tween runs against this base position
  // rather than `g.position` directly, so we can layer a Y bob + Z tilt
  // on top each frame without polluting the tween's accumulator.
  const basePosRef = useRef<{ x: number; y: number; z: number } | null>(null);

  // Keep the cached base emissive color in sync with composition changes
  // so the heat-glow blend uses the per-archetype palette as its 0-blend
  // endpoint rather than the last-seen value.
  useEffect(() => {
    baseEmissiveColorRef.current.set(composition.palette.emissive);
  }, [composition.palette.emissive]);

  const target = useMemo(() => {
    const w = hexToWorld(mech.position, 0);
    return { x: w.x, y: 0.5, z: w.z, yaw: facingToYaw(mech.facing) };
  }, [mech.position.q, mech.position.r, mech.facing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compose per-part × per-slot voxel buckets so we can draw one
  // InstancedMesh per (part, slot) pair. ~24 meshes per mech total.
  const partBuckets = useMemo(() => mergePerPart(composition), [composition]);

  // Heat ramp surfaced to children (HeatShimmer reads this). 0 below
  // HEAT_WARN, ramps linearly to 1 at HEAT_PEAK_FOR_GLOW. Mirrors the
  // emissive-glow computation inside useFrame so the shimmer and the
  // emissive intensity stay in lockstep.
  const heatIntensity =
    mech.heat <= HEAT_WARN
      ? 0
      : Math.min(1, (mech.heat - HEAT_WARN) / (HEAT_PEAK_FOR_GLOW - HEAT_WARN));

  // B14 — set of visual parts whose armor zone is exposed at this tick.
  // Derived from MechState directly so scrubbing the timeline gets the
  // right state for free. Identity-stable across frames where the same
  // zones are exposed; useFrame can read it without ref churn.
  const exposedParts = useMemo(
    () => exposedPartsOf(mech),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      mech.head.exposed,
      mech.front_ct.exposed,
      mech.rear_ct.exposed,
      mech.l_side.exposed,
      mech.r_side.exposed,
      mech.l_leg.exposed,
      mech.r_leg.exposed,
    ],
  );

  useEffect(() => {
    const g = groupRef.current;
    if (!g) return;
    g.position.set(target.x, target.y, target.z);
    g.rotation.y = target.yaw;
    basePosRef.current = { x: target.x, y: target.y, z: target.z };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFrame((_, dt) => {
    const now = performance.now();

    // Kill choreography drives both the emissive write below AND a sag
    // applied with the position write inside the `if (g)` block. Compute
    // once up here so both consumers share the same frame value.
    const killElapsed = killStartRef.current === null ? null : now - killStartRef.current;
    const kill = killChoreography(killElapsed);

    // B8 assembly — reverse-direction dissolve. Wins over kill when
    // both timers are live (shouldn't happen but we'd rather assemble
    // than disintegrate-then-assemble if the caller does something
    // weird). Clears the timer at end-of-window.
    const assemblyElapsed =
      assemblyStartRef.current === null ? null : now - assemblyStartRef.current;
    const assembly = assemblyState(assemblyElapsed);
    if (assemblyElapsed !== null && !assembly.active) {
      assemblyStartRef.current = null;
    }

    // Knockdown prone state — pitch + sag, fall/recover-eased. While
    // knocked down, stagger wobble is suppressed (the chassis is on
    // the floor; it shouldn't tilt-swim).
    const fallElapsed =
      knockdownFallStartRef.current === null ? null : now - knockdownFallStartRef.current;
    const recoverElapsed =
      knockdownRecoverStartRef.current === null ? null : now - knockdownRecoverStartRef.current;
    const knockdown = reducedMotion
      ? { pitchX: 0, sagY: 0 }
      : knockdownState(mech.knocked_down, fallElapsed, recoverElapsed);

    // Stagger wobble — persistent low-Hz nutation while staggered.
    // Suppressed during knockdown and kill (the chassis is occupied).
    const stagger = reducedMotion || mech.knocked_down || killElapsed !== null
      ? { tiltX: 0, tiltZ: 0 }
      : staggerOffset(mech.staggered, now);

    const g = groupRef.current;
    if (g) {
      if (!basePosRef.current) {
        basePosRef.current = { x: g.position.x, y: g.position.y, z: g.position.z };
      }
      const base = basePosRef.current;
      const alpha = Math.min(1, (dt * 1000) / tweenMs);
      base.x += (target.x - base.x) * alpha;
      base.y += (target.y - base.y) * alpha;
      base.z += (target.z - base.z) * alpha;
      g.rotation.y += shortestAngle(g.rotation.y, target.yaw) * alpha;

      // P1 walk-cycle bob: amplitude proportional to remaining tween
      // distance so we settle to zero when the chassis sits still. Sin
      // of wall-clock is cosmetic-only and never feeds state. Reduced-
      // motion skips both bob and tilt (info-bearing effects stay).
      const { bobY, tiltZ } = reducedMotion
        ? { bobY: 0, tiltZ: 0 }
        : walkOffset(target.x - base.x, target.z - base.z, now);
      // Q1 weapon-fire recoil — kick-and-decay push along the negated
      // forward vector + nose-up pitch when `justFired` rises. Reuses
      // the `fireFlashStartRef` timer that P4 already maintains for
      // the status pip.
      const recoilElapsed =
        fireFlashStartRef.current === null ? null : now - fireFlashStartRef.current;
      const { dx: recoilX, dz: recoilZ } = reducedMotion
        ? { dx: 0, dz: 0 }
        : recoilOffset(recoilElapsed, target.yaw);
      const pitchX = reducedMotion ? 0 : recoilPitch(recoilElapsed);

      // P1.2 hit impulse — push along the cached hit normal + a brief
      // chassis-compression scale pulse. Reuses the `flashStartRef`
      // timer that drives the emissive flash so the impulse and the
      // flash stay in lockstep.
      const hitElapsed =
        flashStartRef.current === null ? null : now - flashStartRef.current;
      const {
        dx: hitX,
        dz: hitZ,
        scaleK,
      } = reducedMotion
        ? { dx: 0, dz: 0, scaleK: 1 }
        : hitImpulse(hitElapsed, hitNormalRef.current.dx, hitNormalRef.current.dz);

      g.position.x = base.x + recoilX + hitX;
      // Y composition: walk bob + kill sag + knockdown sag — all
      // additive so concurrent states decay cleanly. Knockdown sag
      // wins visually since the chassis can't be mid-step AND prone.
      g.position.y = base.y + bobY + kill.sagY + knockdown.sagY;
      g.position.z = base.z + recoilZ + hitZ;
      // Rotation composition: walk-tilt + stagger wobble on Z;
      // recoil pitch + knockdown prone-pitch on X.
      g.rotation.z = tiltZ + stagger.tiltZ;
      g.rotation.x = pitchX + stagger.tiltX + knockdown.pitchX;
      g.scale.setScalar(scaleK);
    }

    // Rising-edge triggers.
    if (hitPulse && !prevHitPulseRef.current) {
      flashStartRef.current = now;
      // Sample the hit normal + zone once at impact. Falls back to
      // zero / null so the chassis still scale-pulses + flashes the
      // whole body even without a known shooter direction / zone.
      hitNormalRef.current = hitNormal ?? { dx: 0, dz: 0 };
      flashPartsRef.current = zoneToParts(hitZone);
    }
    prevHitPulseRef.current = hitPulse;
    if (killed && !prevKilledRef.current) {
      killStartRef.current = now;
    } else if (!killed && prevKilledRef.current) {
      // Falling edge: clear the kill timer so a scrub-back / revive
      // (e.g., assemble after kill) doesn't leave the chassis stuck
      // in the kill choreography on subsequent frames.
      killStartRef.current = null;
    }
    prevKilledRef.current = killed;

    // Knockdown rising/falling edges.
    if (mech.knocked_down && !prevKnockedDownRef.current) {
      knockdownFallStartRef.current = now;
      knockdownRecoverStartRef.current = null;
    } else if (!mech.knocked_down && prevKnockedDownRef.current) {
      knockdownRecoverStartRef.current = now;
    }
    prevKnockedDownRef.current = mech.knocked_down;

    // Assembly rising edge → start the assembly timer. We only fire
    // on rising; falling edge leaves the chassis assembled.
    if (assembling && !prevAssemblingRef.current) {
      assemblyStartRef.current = now;
    }
    prevAssemblingRef.current = assembling;

    // Shutdown rising/falling edges → shutdownArc / restartArc.
    if (mech.shutdown && !prevShutdownRef.current) {
      shutdownStartRef.current = now;
      restartStartRef.current = null;
    } else if (!mech.shutdown && prevShutdownRef.current) {
      restartStartRef.current = now;
      shutdownStartRef.current = null;
    }
    prevShutdownRef.current = mech.shutdown;
    if (justFired && !prevJustFiredRef.current) {
      fireFlashStartRef.current = now;
    }
    prevJustFiredRef.current = justFired;

    // Heat-glow intensity (0..1). Linear ramp WARN → PEAK_FOR_GLOW.
    const heatGlow =
      mech.heat <= HEAT_WARN
        ? 0
        : Math.min(1, (mech.heat - HEAT_WARN) / (HEAT_PEAK_FOR_GLOW - HEAT_WARN));

    // Compute the live emissive flash bonus once.
    let flashBonus = 0;
    if (flashStartRef.current !== null) {
      const elapsed = now - flashStartRef.current;
      if (elapsed < FLASH_DURATION_MS) {
        const t = 1 - elapsed / FLASH_DURATION_MS;
        flashBonus = FLASH_PEAK_INTENSITY_BONUS * t;
      } else {
        flashStartRef.current = null;
      }
    }

    // B6 — iterate every (part, slot) cell. Zone-localized flash
    // applies only to parts in `flashPartsRef`; all parts still pick
    // up the kill dissolve, heat, and shutdown writes.
    const flashParts = flashPartsRef.current;
    // B14 — breach pulse phase shared across all exposed parts so the
    // breach reads as a single coherent flicker rather than per-part
    // noise. Sin returns -1..1; we map to 0..1 via 0.5 + 0.5*sin.
    const breachPulse =
      0.5 + 0.5 * Math.sin(now * 0.001 * BREACH_PULSE_HZ * Math.PI * 2);
    const breachEmissiveBonus =
      BREACH_BASE_BONUS + BREACH_PULSE_AMP * breachPulse;
    const breachColorK = 0.6 + 0.3 * breachPulse;
    for (const part of ALL_PARTS) {
      const partFlashes = flashParts === null || flashParts.includes(part);
      const partExposed = exposedParts.has(part);
      for (const slot of SLOT_ORDER) {
        const mesh = meshRefs.current[part][slot];
        if (!mesh) continue;
        const mat = mesh.material as MaterialHandle;

        // B3 dissolve drives the kill collapse; B8 reuses the same
        // uniform but runs it backward for the assembly choreography.
        const dissolve = getDissolveUniforms(mat);
        if (dissolve) {
          if (assembly.active) {
            dissolve.uProgress.value = assembly.progress;
          } else {
            dissolve.uProgress.value = killElapsed === null ? 0 : 1 - kill.opacity;
          }
        }
        mesh.visible = killElapsed === null || kill.visible;

        if (slot === "emissive") {
          // Steady-state base intensity. shutdown wins over heat.
          let base = mech.shutdown ? EMISSIVE_SHUTDOWN_INTENSITY : EMISSIVE_BASE_INTENSITY;
          // P2 shutdown / restart transition arcs override `base` for
          // the duration of the arc so the chassis blows out + decays
          // into shutdown OR boots up with a flicker into running.
          const shutdownElapsed =
            shutdownStartRef.current === null ? null : now - shutdownStartRef.current;
          const restartElapsed =
            restartStartRef.current === null ? null : now - restartStartRef.current;
          if (shutdownElapsed !== null) {
            const arcK = shutdownArc(shutdownElapsed);
            base = arcK > 0 ? EMISSIVE_BASE_INTENSITY * arcK : EMISSIVE_SHUTDOWN_INTENSITY;
          } else if (restartElapsed !== null) {
            const k = restartArc(restartElapsed);
            base =
              EMISSIVE_SHUTDOWN_INTENSITY +
              (EMISSIVE_BASE_INTENSITY - EMISSIVE_SHUTDOWN_INTENSITY) * k;
          }
          // Heat boosts emissive intensity (a "running hot" glow) and
          // blends the emissive color toward HEAT_TINT_COLOR. Shutdown
          // wins — a shutdown mech is inert regardless of pre-shutdown
          // heat. B6: zone-localized flash only applies to parts in
          // flashPartsRef; others use the base/heat/kill writes only.
          const heatBonus = mech.shutdown ? 0 : 0.8 * heatGlow;
          const partFlashBonus = partFlashes ? flashBonus : 0;
          // B14 — exposed parts add a persistent low-amplitude pulse.
          // Shutdown overrides (the chassis is inert; no breach glow).
          // Kill choreography also wins — the dying chassis is going
          // white, not painting a steady breach signal.
          const breachBonus =
            partExposed && !mech.shutdown && killElapsed === null
              ? breachEmissiveBonus
              : 0;
          if (mat.emissiveIntensity !== undefined) {
            mat.emissiveIntensity =
              base + partFlashBonus + heatBonus + breachBonus + kill.emissiveBoost;
          }
          if (mat.emissive) {
            _scratchColor.copy(baseEmissiveColorRef.current);
            if (heatGlow > 0 && !mech.shutdown) {
              _scratchColor.lerp(HEAT_TINT_COLOR, heatGlow * 0.55);
            }
            // B14 — breach tint lerps the emissive color toward a deep
            // red. Sits below heat in the layering (heat is a global
            // chassis state; breach is per-part and visually distinct
            // via the slow pulse). Skipped during kill so the white
            // blow-out reads clean.
            if (breachBonus > 0 && kill.emissiveColorK === 0) {
              _scratchColor.lerp(BREACH_TINT_COLOR, breachColorK);
            }
            // Kill flash lerps the emissive color toward white. Wins
            // over heat tint when killed (the chassis is being blown
            // out, not just running hot).
            if (kill.emissiveColorK > 0) {
              _scratchColor.lerp(WHITE, kill.emissiveColorK);
            }
            mat.emissive.copy(_scratchColor);
          }
        }
      }
    }


    // P4 status pip — color + scale resolved from the same rising-edge
    // timers we already maintain. Imperative writes to the pip mesh.
    const pip = pipMeshRef.current;
    if (pip) {
      const hitElapsed = flashStartRef.current === null ? null : now - flashStartRef.current;
      const fireElapsed = fireFlashStartRef.current === null ? null : now - fireFlashStartRef.current;
      const pipState = resolvePipState(
        {
          shutdown: mech.shutdown,
          hitElapsedMs: hitElapsed,
          fireElapsedMs: fireElapsed,
          heat: mech.heat,
        },
        now,
        reducedMotion,
      );
      if (pipState === null) {
        pip.visible = false;
      } else {
        pip.visible = kill.visible;
        const mat = pip.material as MeshBasicMaterial;
        if (mat?.color) mat.color.set(pipState.color);
        if (killElapsed !== null) {
          mat.transparent = true;
          mat.opacity = kill.opacity;
        } else if (mat.opacity !== 1) {
          mat.opacity = 1;
          mat.transparent = false;
        }
        pip.scale.setScalar(pipState.scale);
      }
    }

    // Garbage-collect fire-flash once it expires so the pip drops off
    // back to the heat/idle state.
    // Hold the timer for the longer of the recoil window and the
    // pip-flash window (each consumer clamps its own visible duration
    // via `resolvePipState` / `recoilEnvelope`).
    if (
      fireFlashStartRef.current !== null &&
      now - fireFlashStartRef.current > Math.max(RECOIL_DURATION_MS, PIP_FIRE_FLASH_MS)
    ) {
      fireFlashStartRef.current = null;
    }
  });

  return (
    <group ref={groupRef} name={`mech-${mech.player}`}>
      {/* B6 — render one InstancedMesh per (part, slot). Empty
          buckets render nothing (VoxelInstances early-returns when
          count is 0). meshRefs is indexed [part][slot] so the parent
          useFrame can address each cell independently. */}
      {ALL_PARTS.map((part) =>
        SLOT_ORDER.map((slot) => {
          const voxels = partBuckets[part]?.[slot] ?? [];
          return (
            <VoxelInstances
              key={`${part}:${slot}`}
              slot={slot}
              voxels={voxels}
              color={composition.palette[slot]}
              registerMesh={(m) => {
                meshRefs.current[part][slot] = m;
              }}
            />
          );
        }),
      )}
      {/* P4 status pip — parent useFrame mutates color/scale/visibility. */}
      <mesh ref={pipMeshRef} position={[0, PIP_LOCAL_Y, 0]} visible={false}>
        <sphereGeometry args={[PIP_RADIUS, 12, 12]} />
        <meshBasicMaterial color="#ffffff" transparent={false} />
      </mesh>
      {/* P3 persistent state indicators — each self-suppresses when its
          MechState flag is off, so we can mount unconditionally. */}
      <TaggedMarker tagged={mech.tagged} accent={composition.palette.accent} />
      <EvasionMotes pips={mech.evasion_pips} accent={composition.palette.accent} />
      <SmokeWisps active={mech.smoke_active} />
      <ScanLine active={mech.scanned_this_turn} accent={composition.palette.accent} />
      {/* B4 status-effect auras — burning rides smoke_active (no
          dedicated burn flag on MechState yet), frozen is an
          explicit opt-in prop, EMP rides ecm_bubble_active. */}
      <BurningAura active={mech.smoke_active} />
      <FrozenAura active={frozen} />
      <EmpAura active={mech.ecm_bubble_active} />
      {/* B5 heat shimmer — localized refraction lens. Strength ramps
          from HEAT_WARN upward; below threshold the mesh hides
          itself (no transparency cost). */}
      <HeatShimmer intensity={heatIntensity} />
      {/* B7 crit-part smoke — one PartSmoke per damaged part, each
          severity-scaled. Local positions are approximations of the
          part anchors (the chassis composer doesn't expose a part
          centroid yet; once it does we can read those instead). */}
      <PartSmoke severity={mech.engine_crits} origin={[0, 0.55, 0]} />
      <PartSmoke severity={mech.gyro_crits} origin={[0, 1.0, 0]} color="#2a2a2a" />
      <PartSmoke
        severity={mech.l_leg_actuator_damage}
        origin={[-0.12, 0.2, 0]}
        color="#2a1f1f"
      />
      <PartSmoke
        severity={mech.r_leg_actuator_damage}
        origin={[0.12, 0.2, 0]}
        color="#2a1f1f"
      />
      {/* B7 target-lock reticle — accent-colored torus floating above
          the head while target_lock_on > 0. Pulses + rotates;
          multiple locks scale up. */}
      <TargetLockReticle
        locks={mech.target_lock_on}
        accent={composition.palette.accent}
      />
    </group>
  );
}

interface VoxelInstancesProps {
  slot: MaterialSlot;
  voxels: ReadonlyArray<Voxel>;
  color: string;
  registerMesh: (mesh: InstancedMesh | null) => void;
}

function VoxelInstances({ slot, voxels, color, registerMesh }: VoxelInstancesProps) {
  const meshRef = useRef<InstancedMesh>(null);
  const count = voxels.length;

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    registerMesh(mesh);
    const m = new Matrix4();
    voxels.forEach((v, i) => {
      m.makeTranslation(v.x * VOXEL_SCALE, v.y * VOXEL_SCALE, v.z * VOXEL_SCALE);
      mesh.setMatrixAt(i, m);
    });
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    // Per-material defaults; ArchetypeMech's parent useFrame may
    // mutate `emissiveIntensity` for the emissive slot live.
    const mat = mesh.material as MaterialHandle;
    if (mat?.color && "set" in mat.color) {
      mat.color.set(color);
    }
    if (slot === "emissive") {
      mat.emissive?.set(color);
      if (mat.emissiveIntensity !== undefined) mat.emissiveIntensity = EMISSIVE_BASE_INTENSITY;
      if (mat.metalness !== undefined) mat.metalness = 0.1;
      if (mat.roughness !== undefined) mat.roughness = 0.4;
    } else {
      if (mat.metalness !== undefined) mat.metalness = slot === "primary" ? 0.6 : 0.3;
      if (mat.roughness !== undefined) mat.roughness = slot === "secondary" ? 0.7 : 0.5;
    }
    // B3 — patch the material with a dissolve discard + edge-band
    // uniform set. Parent useFrame drives uProgress on kill.
    applyDissolvePatch(mat as unknown as import("three").MeshStandardMaterial, {
      progress: 0,
      edgeColor: "#ffffff",
      edgeWidth: 0.08,
    });
    return () => registerMesh(null);
  }, [voxels, color, slot, registerMesh]);

  if (count === 0) return null;

  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, count]} castShadow receiveShadow>
      <boxGeometry args={[VOXEL_SCALE, VOXEL_SCALE, VOXEL_SCALE]} />
      <meshStandardMaterial color={color} />
    </instancedMesh>
  );
}

/**
 * B6 — per-part × per-slot voxel bucketing. Each part contributes
 * voxels grouped by material slot so each part renders independently
 * (4 InstancedMeshes per part, ~24 per mech). Anchors are folded
 * into the voxel positions so the parent group transform is shared.
 */
function mergePerPart(
  c: MechComposition,
): Record<PartSlot, Partial<Record<MaterialSlot, Voxel[]>>> {
  const out: Record<PartSlot, Partial<Record<MaterialSlot, Voxel[]>>> = {
    chassis: {},
    legs: {},
    left_arm: {},
    right_arm: {},
    head: {},
    mount: {},
  };
  for (const [partKey, part] of Object.entries(c.parts) as Array<[
    PartSlot,
    typeof c.parts[PartSlot],
  ]>) {
    const buckets = out[partKey];
    for (const v of part.voxels) {
      const arr = buckets[v.slot] ?? (buckets[v.slot] = []);
      arr.push({
        x: v.x + part.anchor.x,
        y: v.y + part.anchor.y,
        z: v.z + part.anchor.z,
        slot: v.slot,
      });
    }
  }
  return out;
}

function shortestAngle(from: number, to: number): number {
  const two = Math.PI * 2;
  let d = (((to - from) % two) + two) % two;
  if (d > Math.PI) d -= two;
  return d;
}

